/**
 * Vessel display-label helpers (fleet-card redesign). VESSELS-ONLY SLICE.
 *
 * Small pure helpers for turning stored vessel fields into human labels on the
 * Fleet landing card. The type/category maps mirror VesselSelector.js so the
 * card and the switcher read identically; extracted here so both could later
 * share one source (VesselSelector still owns its own copy today — no behaviour
 * change to it in this slice).
 */

// Mirrors VesselSelector.getVesselTypeDisplay. Falls back to a title-cased
// version of the raw stored value for any type not in the map (e.g. 'workboat').
const VESSEL_TYPE_LABELS = {
  rib: 'RIB',
  tug: 'Tug',
  pilot: 'Pilot Boat',
  pax: 'Passenger',
  ctv: 'CTV',
  survey: 'Survey',
  support: 'Support',
  patrol: 'Patrol',
  workboat: 'Workboat',
};

/**
 * @param {string} [type] raw vessel.type
 * @returns {string|null} display label, or null when unset
 */
export function getVesselTypeLabel(type) {
  if (!type) return null;
  const key = String(type).toLowerCase().trim();
  if (VESSEL_TYPE_LABELS[key]) return VESSEL_TYPE_LABELS[key];
  // Title-case the raw value as a safe fallback.
  return key.charAt(0).toUpperCase() + key.slice(1);
}

/**
 * @param {string|number} [category] raw vessel.category ('cat2', '2', 2, 'IWW')
 * @returns {string|null} e.g. 'Cat 2', 'IWW', or null when unset
 */
export function getVesselCategoryLabel(category) {
  if (category === null || category === undefined || category === '') return null;
  const str = String(category).trim();
  if (str.toLowerCase() === 'iww') return 'IWW';
  if (str.toLowerCase().startsWith('cat')) {
    const num = str.slice(3).trim();
    return num ? `Cat ${num}` : null;
  }
  return `Cat ${str}`;
}

/**
 * Build the vessel particulars subtitle line for the card:
 *   "Workboat · Cat 2 · Belfast · 14.9m LOA"
 * Only includes the parts that are actually stored (no invented fields).
 *
 * @param {object} vessel enriched vessel object (VesselContext)
 * @returns {string[]} ordered list of present particulars, ready to join with ' · '
 */
export function getVesselParticulars(vessel) {
  if (!vessel) return [];
  const parts = [];
  const type = getVesselTypeLabel(vessel.type);
  if (type) parts.push(type);
  const cat = getVesselCategoryLabel(vessel.category);
  if (cat) parts.push(cat);
  if (vessel.homePort) parts.push(String(vessel.homePort));
  // lengthOverall is the canonical LOA field (vesselConfig schema, FT-29).
  const loa = vessel.lengthOverall ?? vessel.length;
  if (loa !== undefined && loa !== null && String(loa).trim() !== '') {
    parts.push(`${loa}m LOA`);
  }
  return parts;
}
